import React, { useState, useEffect } from 'react';
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import { AccessTime as AccessTimeIcon } from '@mui/icons-material';

interface EventCountdownProps {
  targetDate: string;
  variant?: 'default' | 'compact' | 'large';
  textColor?: string;
}

const EventCountdown = ({ targetDate, variant = 'default', textColor = '#fff' }: EventCountdownProps) => {
  const [countdown, setCountdown] = useState<string>('Chargement...');
  const theme = useTheme();
  
  // Détecter si l'appareil est un mobile avec useMediaQuery
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    let interval: NodeJS.Timeout;
    let timerActive = true;

    const updateTimer = () => {
      if (!timerActive) return;
      
      const now = new Date();
      const target = new Date(targetDate);
      const diff = target.getTime() - now.getTime();
      
      if (diff <= 0) {
        setCountdown('En cours');
        clearInterval(interval);
        return;
      }
      
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
      const minutes = Math.floor((diff / (1000 * 60)) % 60);
      const seconds = Math.floor((diff / 1000) % 60);
      
      if (variant === 'compact') {
        setCountdown(
          `${days}j ${hours.toString().padStart(2, '0')}h ${minutes.toString().padStart(2, '0')}m`
        );
      } else if (variant === 'large') {
        // Sur mobile, on n'affiche pas les secondes pour éviter le retour à la ligne
        if (isMobile) {
          setCountdown(
            `${days}j ${hours.toString().padStart(2, '0')}h ${minutes.toString().padStart(2, '0')}m`
          );
        } else {
          setCountdown(
            `${days}j ${hours.toString().padStart(2, '0')}h ${minutes.toString().padStart(2, '0')}m ${seconds.toString().padStart(2, '0')}s`
          );
        }
      } else {
        // Pour les autres variantes, même logique
        if (isMobile) {
          setCountdown(
            `${days}j ${hours.toString().padStart(2, '0')}h ${minutes.toString().padStart(2, '0')}m`
          );
        } else {
          setCountdown(
            `${days}j ${hours.toString().padStart(2, '0')}h ${minutes.toString().padStart(2, '0')}m ${seconds.toString().padStart(2, '0')}s`
          );
        }
      }
    };
    
    updateTimer();
    interval = setInterval(updateTimer, 1000);
    
    return () => {
      timerActive = false;
      clearInterval(interval);
    };
  }, [targetDate, variant]);

  if (variant === 'large') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Typography 
          variant="h4" 
          sx={{ 
            color: textColor, 
            fontWeight: 'bold',
            // Taille de police responsive selon la taille d'écran
            fontSize: { 
              xs: '1rem',  // Mobile
              sm: '1.25rem',    // Tablette
              md: '1.5rem',  // Petits écrans
              lg: '1.75rem'     // Grands écrans
            },
            textShadow: '0 0 10px rgba(52, 152, 219, 0.8)',
            letterSpacing: '0.05em'
          }}
        >
          {countdown}
        </Typography>
      </Box>
    );
  }
  
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <AccessTimeIcon fontSize="small" sx={{ color: textColor }} />
      <Typography variant="body2" sx={{ color: textColor, fontWeight: 'medium' }}>
        {countdown}
      </Typography>
    </Box>
  );
};

export default EventCountdown;
